import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Pencil, Wrench } from 'lucide-react';
import { updateMaintenanceRequest } from '@/lib/api';
import { toast } from 'sonner';

// Maintenance Request Editor
export function MaintenanceEditPopup({ open, onClose, request, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (request) {
      setForm({ ...request });
      setEditing(false);
    }
  }, [request]);

  if (!request) return null;

  const handleSaveClick = () => setShowConfirm(true);

  const handleConfirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      await updateMaintenanceRequest(request.id, form);
      toast.success('Maintenance request updated');
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="maintenance-edit-popup">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading flex items-center gap-2">
              <Wrench className="h-4 w-4" /> Maintenance Request
            </DialogTitle>
            <DialogDescription>{request.title || request.description?.slice(0, 60)}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-3 pb-4">
              {!editing ? (
                <div className="space-y-2 text-sm">
                  <div><span className="text-xs text-muted-foreground">Description</span><p className="font-medium">{request.description || '\u2014'}</p></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-xs text-muted-foreground">Status</span><p><Badge variant={request.status === 'completed' ? 'default' : 'secondary'} className="text-xs">{request.status || 'open'}</Badge></p></div>
                    <div><span className="text-xs text-muted-foreground">Priority</span><p className="font-medium">{request.priority || '\u2014'}</p></div>
                    <div><span className="text-xs text-muted-foreground">Assigned To</span><p className="font-medium">{request.assigned_to || '\u2014'}</p></div>
                    <div><span className="text-xs text-muted-foreground">Unit</span><p className="font-medium">{request.unit_number ? `Unit ${request.unit_number}` : '\u2014'}</p></div>
                  </div>
                  {request.notes && <div><span className="text-xs text-muted-foreground">Notes</span><p>{request.notes}</p></div>}
                </div>
              ) : (
                <div className="space-y-3">
                  <div><Label className="text-xs">Description</Label><Textarea value={form.description || ''} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="text-sm mt-0.5" data-testid="maint-edit-description" /></div>
                  <div><Label className="text-xs">Status</Label>
                    <Select value={form.status || 'open'} onValueChange={v => setForm(p => ({...p, status: v}))}>
                      <SelectTrigger className="h-8 text-sm mt-0.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Priority</Label>
                    <Select value={form.priority || 'medium'} onValueChange={v => setForm(p => ({...p, priority: v}))}>
                      <SelectTrigger className="h-8 text-sm mt-0.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Assigned To</Label><Input value={form.assigned_to || ''} onChange={e => setForm(p => ({...p, assigned_to: e.target.value}))} className="h-8 text-sm mt-0.5" data-testid="maint-edit-assigned" /></div>
                  <div><Label className="text-xs">Notes</Label><Textarea value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="text-sm mt-0.5" data-testid="maint-edit-notes" /></div>
                </div>
              )}
            </div>
          </ScrollArea>
          <Separator />
          <DialogFooter className="flex-row gap-2 sm:justify-end">
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => { setForm({...request}); setEditing(false); }}>Cancel</Button>
                <Button size="sm" onClick={handleSaveClick} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5" data-testid="maint-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Save Changes?</AlertDialogTitle><AlertDialogDescription>Are you sure you want to save the changes?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleConfirmSave}>Save</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

